<template>
  <div>
    <ValidationObserver ref="observer">
      <div>
        <v-row
          no-gutters
          v-for="(beneficiary, index) in beneficiaries"
          :key="index"
          class="beneficiary-row"
        >
          <v-col :cols="withPercentages ? 5 : 11">
            <div
              style="
                width: 100%;
                border-radius: 4px;
                box-shadow: 0px 0px 5px 0px #00000014;
                padding: 20px;
                padding-top: 12px;
                padding-bottom: 12px;
                background-color: white;
              "
            >
              <v-row>
                <v-col
                  cols="12"
                  style="color: #141414; font-size: 16px"
                  class="d-flex"
                >
                  {{ beneficiaries[index].beneficiaryDisplayName }}
                  <v-icon
                    v-if="!withPercentages"
                    class="cancel-icon"
                    @click="beneficiaryRemoved(index)"
                    >{{ icons.mdiCloseCircle }}</v-icon
                  >
                </v-col>
              </v-row>
            </div>
          </v-col>
          <v-col
            v-if="withPercentages && !distributeEqually"
            class="with-percentages-col col-3"
            offset="1"
          >
            <div class="percentage-container">
              <ValidationProvider
                :id="uniqueKey(beneficiary, index)"
                :vid="uniqueKey(beneficiary, index)"
                v-slot="{ errors }"
                name="percentage"
                rules="required|max_value:100|integer|min_value:1"
              >
                <TextField
                  v-model="beneficiary.percentage"
                  :suffix="'%'"
                  :type="'number'"
                  :errors="errors"
                  :name="'percentage'"
                  :calculatorMargins="true"
                  :max="100"
                  :min="1"
                />
              </ValidationProvider>
            </div>
          </v-col>
          <v-col v-if="withPercentages" cols="2" offset="1" class="mt-3">
            <GradientButton
              :title="'Delete'"
              :small="true"
              :button-size="'small'"
              :fill="false"
              @gradientButtonClicked="beneficiaryRemoved(index)"
            />
          </v-col>
        </v-row>
      </div>

      <v-row
        no-gutters
        v-if="beneficiaries.length && withPercentages"
        class="total-percentage-container"
      >
        <v-col cols="6">
          <h2 class="total-percentage-title">Total Percentage</h2>
        </v-col>
        <v-col cols="6">
          <div class="total-percentage">{{ totalPercentage }}%</div>
          <ValidationProvider
            id="total_percentage"
            v-slot="{ errors }"
            name="total_percentage"
            :rules="{ is: 100 }"
          >
            <h1 style="display: none">{{ totalPercentageHidden }}</h1>
            <input v-model="totalPercentage" type="hidden" disabled />
            <span class="error-text">{{
              errors.length ? 'The total percentage should be 100%.' : ''
            }}</span>
          </ValidationProvider>
        </v-col>
      </v-row>

      <div
        v-if="beneficiaries.length && withPercentages"
        class="distribute-equally-container"
      >
        <Checkbox
          v-model="distributeEqually"
          :label="`Distribute equally among all parties`"
          color="primary"
          @change="distributeEquallyChanged"
        />
      </div>
    </ValidationObserver>

    <div v-if="!hideAddBeneficiaryBtns">
      <v-row
        v-if="!whitelabel.omDesign"
        no-gutters
        class="add-buttons-container"
      >
        <v-col class="add-asset-btn">
          <v-btn
            class="add-item-btn"
            outlined
            large
            depressed
            @click.stop="showPersonDialog = true"
          >
            <v-icon color="primary">{{ icons.mdiPlusCircle }}</v-icon>
            <span>Add person</span>
          </v-btn>
        </v-col>

        <v-col class="add-asset-btn">
          <v-btn
            class="add-item-btn"
            outlined
            large
            depressed
            @click.stop="showCharityDialog = true"
          >
            <v-icon color="primary">{{ icons.mdiPlusCircle }}</v-icon>
            <span>Add Charity</span>
          </v-btn>
        </v-col>
        <v-col class="add-asset-btn">
          <v-btn
            v-if="showAddTrust"
            class="add-item-btn"
            outlined
            large
            depressed
            @click.stop="showTrustDialog = true"
          >
            <v-icon color="primary">{{ icons.mdiPlusCircle }}</v-icon>
            <span>Add Trust</span>
          </v-btn>
        </v-col>
      </v-row>
      <v-row v-else class="om-add-buttons-container mt-5 mb-5 ml-0">
        <AlternativeButton
          :title="'Add Person'"
          @gradientButtonClicked="showPersonDialog = true"
        />
        <AlternativeButton
          :title="'Add Charity'"
          @gradientButtonClicked="showCharityDialog = true"
        />
        <AlternativeButton
          v-if="showAddTrust"
          :title="'Add Trust'"
          @gradientButtonClicked="showTrustDialog = true"
        />
      </v-row>
    </div>

    <PersonDialog
      v-model="showPersonDialog"
      relation="HEIR"
      :create-new-relationship="false"
      :person-to-update="{}"
      :people-list-for-dialog="peopleExcludingAlreadySelected"
      @existingPersonSelected="personSelected"
      @personCreated="personSelected"
    />
    <CharityDialog
      v-model="showCharityDialog"
      :existing-charities="charitiesExcludingAlreadySelected"
      @existingCharitySelected="charitySelected"
      @charityCreated="charitySelected"
    />
    <TrustDialog
      v-model="showTrustDialog"
      :existing-trusts="trustsExcludingAlreadySelected"
      @existingTrustSelected="trustSelected"
      @trustCreated="trustSelected"
    />
  </div>
</template>

<script>
  import PersonDialog from '../people/PersonDialog.vue';
  import CharityDialog from '../people/CharityDialog.vue';
  import TrustDialog from '../people/TrustDialog.vue';
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';

  export default {
    name: 'ChooseBeneficiary',
    components: {
      PersonDialog,
      CharityDialog,
      TrustDialog,
    },
    props: {
      beneficiaries: {
        type: Array,
        required: true,
      },
      limitBeneficiaries: {
        type: Boolean,
        default: true,
      },
      validateForm: {
        type: Boolean,
        required: false,
      },
      withPercentages: {
        type: Boolean,
        required: true,
      },
    },
    data() {
      return {
        showPersonDialog: false,
        showCharityDialog: false,
        showTrustDialog: false,
        testator: {},
        totalPercentage: null,
        distributeEqually: false,
      };
    },
    mounted() {
      this.$nextTick(() => {
        this.checkIfEquallyDistributed();
      });
    },
    watch: {
      beneficiaries: {
        handler() {
          // If distributeEqually is checked, verify it's still equal after manual changes
          if (this.distributeEqually) {
            this.checkIfEquallyDistributed();
          }
        },
        deep: true,
      },
    },
    computed: {
      isMobile() {
        return this.$isMobileDevice();
      },
      totalPercentageHidden() {
        const percentage = this.beneficiaries.reduce(
          (accumulator, obj) => accumulator + parseInt(obj.percentage || 0),
          0
        );
        if (percentage) {
          this.totalPercentage = percentage;
          // Uncheck distribute equally if total exceeds 100%
          if (percentage > 100 && this.distributeEqually) {
            this.distributeEqually = false;
          }
          return percentage;
        } else {
          this.totalPercentage = 0;
          return '0';
        }
      },
      peopleExcludingAlreadySelected() {
        if (!this.testator.people) {
          return undefined;
        }
        return this.testator.people.map(p => {
          return this.beneficiaries.find(
            b => b.beneficiaryType == 'Person' && p.id == b.beneficiaryId
          )
            ? { ...p, disabledMessage: 'Person already selected' }
            : p;
        });
      },
      charitiesExcludingAlreadySelected() {
        if (!this.testator.charities) {
          return undefined;
        }
        return this.testator.charities.map(p => {
          return this.beneficiaries.find(
            b => b.beneficiaryType == 'Charity' && p.id == b.beneficiaryId
          )
            ? { ...p, disabledMessage: 'Charity already selected' }
            : p;
        });
      },
      trustsExcludingAlreadySelected() {
        if (!this.testator.trusts) {
          return undefined;
        }
        return this.testator.trusts.map(p => {
          return this.beneficiaries.find(
            b => b.beneficiaryType == 'Trust' && p.id == b.beneficiaryId
          )
            ? { ...p, disabledMessage: 'Trust already selected' }
            : p;
        });
      },
      hideAddBeneficiaryBtns() {
        if (this.withPercentages) {
          return false;
        } else {
          return (
            (this.limitBeneficiaries && this.beneficiaries.length) ||
            (this.beneficiaries.length &&
              this.beneficiaries[0].beneficiaryType != 'Person')
          );
        }
      },
      showAddTrust() {
        return this.testator.willCountry != 'GB';
      },
    },
    created() {
      // using native JSON functions removes reactivity
      // so we can clone an object without mutating the original source
      if (this.beneficiary)
        this.chosenBeneficiary = JSON.parse(JSON.stringify(this.beneficiary));
    },
    watch: {
      validateForm: function (newValue) {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.$emit('beneficiariesValidated', true);
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
    },
    methods: {
      checkIfEquallyDistributed() {
        if (!this.beneficiaries || this.beneficiaries.length === 0) {
          this.distributeEqually = false;
          return;
        }

        const validPercentages = this.beneficiaries.filter(
          p =>
            p.percentage &&
            p.percentage !== '' &&
            !isNaN(parseInt(p.percentage))
        );

        if (validPercentages.length !== this.beneficiaries.length) {
          this.distributeEqually = false;
          return;
        }

        const equalPercentage = Math.floor(100 / this.beneficiaries.length);
        const remainder = 100 % this.beneficiaries.length;

        const isEqual = this.beneficiaries.every((beneficiary, index) => {
          const expectedPercentage =
            equalPercentage + (index < remainder ? 1 : 0);
          return parseInt(beneficiary.percentage) === expectedPercentage;
        });

        this.distributeEqually = isEqual;
      },
      distributeEquallyChanged() {
        if (!this.beneficiaries || this.beneficiaries.length === 0) {
          return;
        }

        if (this.distributeEqually) {
          const equalPercentage =
            this.beneficiaries.length > 0
              ? Math.floor(100 / this.beneficiaries.length)
              : 0;
          const remainder =
            this.beneficiaries.length > 0 ? 100 % this.beneficiaries.length : 0;

          // Use Vue.set or $set to ensure reactivity
          this.beneficiaries.forEach((beneficiary, index) => {
            this.$set(
              beneficiary,
              'percentage',
              equalPercentage + (index < remainder ? 1 : 0)
            );
          });
        } else {
          // Reset percentages to empty string when unchecking
          this.beneficiaries.forEach(beneficiary => {
            this.$set(beneficiary, 'percentage', '');
          });
        }
      },
      personSelected(person) {
        this.beneficiarySelected(person, 'Person');
      },
      charitySelected(charity) {
        this.beneficiarySelected(charity, 'Charity');
      },
      trustSelected(trust) {
        this.beneficiarySelected(trust, 'Trust');
      },
      beneficiarySelected(beneficiary, beneficiaryType) {
        this.$emit('beneficiarySelected', {
          beneficiaryType: beneficiaryType,
          beneficiaryId: beneficiary.id,
          beneficiaryDisplayName: beneficiary.displayName,
        });
        if (this.distributeEqually) {
          this.distributeEquallyChanged();
        }
      },
      beneficiaryRemoved(index) {
        this.$emit('beneficiaryRemoved', index);
        if (this.distributeEqually) {
          this.distributeEquallyChanged();
        }
      },
      uniqueKey(beneficiary, index) {
        return `${beneficiary.beneficiaryId}-${index}`;
      },
    },
    apollo: {
      testator: {
        query: TESTATOR_QUERY,
        variables() {
          return {
            id: this.testatorId,
          };
        },
      },
    },
  };
</script>

<style scoped lang="scss">
  .add-asset-btn {
    padding-left: 10px !important;
    padding-right: 10px !important;
    width: 33%;
  }

  @media screen and (max-width: 1263px) and (min-width: 960px) {
    .add-asset-btn {
      padding-left: 4px !important;
      padding-right: 4px !important;
    }
  }

  .list-item {
    max-width: 450px;
  }

  .add-item-btn {
    width: 100%;
    max-width: 450px;
  }

  .percentage-container {
    padding-top: 8px;
    padding-bottom: 8px;
    span {
      display: block;
      .v-text-field {
        padding-top: 0;
        margin-top: 0;
      }
    }
  }

  .percentage-container-normal {
    margin-top: 10px !important;
  }
  .percentage-container-error {
    margin-top: 20px !important;
  }

  .total-percentage-title {
    line-height: 28px;
  }

  .total-percentage {
    background-color: #fbf398;
    border: 2px solid #fced56;
    border-radius: 10px;
    font-size: 18px;
    font-weight: bold;
    padding: 10px;
    text-align: center;
    width: 100%;
  }
  .total-percentage-container {
    margin-top: 30px;
    margin-bottom: 30px;
    display: flex;
    align-items: center;
  }
  .cancel-icon {
    color: black;
    fill: black;
    margin-left: auto;
  }
  .beneficiary-row {
    margin: 24px 12px 24px 0;
  }
  @media screen and (min-width: 965px) and (max-width: 1262px) {
    .add-buttons-container {
      display: flex;
      flex-direction: column;
      gap: 12px;
      .add-asset-btn {
        width: 100%;
      }
    }
  }
  @media screen and (min-width: 0px) and (max-width: 486px) {
    .add-buttons-container {
      display: flex;
      flex-direction: column;
      gap: 12px;
      .add-asset-btn {
        width: 100%;
      }
    }
  }
  .om-add-buttons-container {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }
  .with-percentages-col {
    margin-top: -12px;
  }
  .distribute-equally-container {
    margin-top: 20px;
    margin-bottom: 20px;
  }
</style>
